/**
 * A message from a contact's profile, opened in place.
 *
 * A communication row on a profile used to be a label — the message it named
 * could not be reached from the CRM at all. The row now carries the intake
 * record's id, and this reads that record and shows its stored source through
 * the same viewer Intake and the dossier use, so a reader verifying "who said
 * this" stays on the contact.
 *
 * Every state the record can be in is drawn honestly: reading, unreadable,
 * and "on file but no stored source" — that last one still offers the intake
 * queue, which holds the parsed text even when no original file was kept.
 */
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ExternalLink, Loader2 } from 'lucide-react'

import { ingestionService } from '@/services/ingestion'
import SourceViewerDialog from '@/components/intake/SourceViewerDialog'
import { crmErrorDetail } from '@/services/crm'
import DialogShell from './DialogShell'
import { emailHref } from './contactLinks'
import { initialShaFor } from './crmFields'

function IntakeLink({ ingestionId, children }: { ingestionId: string; children: ReactNode }) {
  const href = emailHref(ingestionId)
  if (!href) return null
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1 rounded text-[11px] text-amber-400 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
    >
      <ExternalLink size={10} />
      {children}
    </Link>
  )
}

export default function EmailViewer({ ingestionId, onClose }: { ingestionId: string; onClose: () => void }) {
  const record = useQuery({
    queryKey: ['ingestion', 'record', ingestionId],
    queryFn: () => ingestionService.getById(ingestionId),
  })

  if (record.isPending) {
    return (
      <DialogShell label="Opening message" onClose={onClose} width="md" zIndex={110}>
        <div className="flex items-center gap-2 px-4 py-6 text-[11px] text-stone-400">
          <Loader2 size={12} className="motion-safe:animate-spin text-amber-400" />
          Reading the stored message…
        </div>
      </DialogShell>
    )
  }

  if (record.isError) {
    return (
      <DialogShell label="Message could not be read" onClose={onClose} width="md" zIndex={110}>
        <div className="space-y-2 px-4 py-4">
          <p className="text-[11px] text-red-300">
            {crmErrorDetail(record.error, 'The intake record could not be read.')}
          </p>
          <IntakeLink ingestionId={ingestionId}>Open in Intake</IntakeLink>
        </div>
      </DialogShell>
    )
  }

  const files = record.data.rawFilePaths ?? []
  if (files.length === 0) {
    return (
      <DialogShell label="No stored source" onClose={onClose} width="md" zIndex={110}>
        <div className="space-y-2 px-4 py-4">
          <p className="text-[11px] text-stone-300">
            {record.data.sourceSubject ? (
              <span className="text-stone-200">{record.data.sourceSubject}</span>
            ) : (
              'This message'
            )}{' '}
            is on record, but no original file was stored for it. The parsed text is on the intake queue.
          </p>
          <IntakeLink ingestionId={ingestionId}>Open in Intake</IntakeLink>
        </div>
      </DialogShell>
    )
  }

  // Portalled: the contact window is an overflow-hidden, backdrop-filtered
  // dialog, and a fixed-position viewer nested inside one is clipped to it.
  // Wrapped as a modal dialog so the window's shell knows something sits on
  // top and leaves Escape and Tab to it.
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Message source">
      <SourceViewerDialog ingestionId={ingestionId} files={files} initialSha={initialShaFor(files)} onClose={onClose} />
    </div>,
    document.body,
  )
}
